import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import ActiveProgramOverview from './ActiveProgramOverview';
import AddToHomeScreen from './AddToHomeScreen';
import AIAnalysis from './AIAnalysis';
import Auth from './Auth';
import BodyWeightTracker from './BodyWeightTracker';
import BottomNav from './BottomNav';
import { GuideSheet } from './ExerciseGuidePreview';
import MuscleDistribution from './MuscleDistribution';
import ProgramBuilder from './ProgramBuilder';
import ProgressCharts from './ProgressCharts';
import WorkoutSession from './WorkoutSession';
import { displayNameForGuide, searchGuideExercises } from './exerciseGuide';
import { auth, db } from './firebase';
import PRESET_EXERCISES, { TIMED_EXERCISES, getExerciseMuscles } from './exercises';
import { parseEnGbDate, getWeekStart } from './dateUtils';
import { IconShare, IconTrash } from './icons';
import { workoutVolume, muscleSetsInRange } from './muscleStats';
import { buildSessionRecap } from './sessionRecap';
import { shareStatsCard, shareWorkoutCard } from './shareCard';
import translations from './translations';
import {
  countLoggedSets,
  formatDuration,
  isSetComplete,
  isSetFilled,
  isSetLogged,
  isTimedExercise,
  parseSetCount,
  workoutHasContent,
  workoutHasLoggedSets,
} from './workoutUtils';
import logoDark from './logo-dark.png';
import logoLight from './logo-light.png';
import './App.css';

function emptySet(timed, prev) {
  if (timed) return { durationMinutes: prev?.durationMinutes || '', completed: false };
  return {
    reps: prev?.reps || '',
    weight: prev?.weight || '',
    rir: prev?.rir || '',
    completed: false,
  };
}

function App() {
  const [workoutLog, setWorkoutLog] = useState({});
  const [exerciseName, setExerciseName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showLoadPrevious, setShowLoadPrevious] = useState(false);
  const [todaysExercises, setTodaysExercises] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});
  const [workoutFinished, setWorkoutFinished] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') !== 'false');
  const [advancedTracking, setAdvancedTracking] = useState(() => localStorage.getItem('advancedTracking') === 'true');
  const [weightUnit, setWeightUnit] = useState(() => localStorage.getItem('weightUnit') || 'kg');
  const [ignoringProgram, setIgnoringProgram] = useState(false);
  const [activeProgram, setActiveProgram] = useState(() => {
    try {
      const saved = localStorage.getItem('activeProgram');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [newExerciseTrackingType, setNewExerciseTrackingType] = useState('weighted');
  const [customMuscle, setCustomMuscle] = useState('');
  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const [draggedExerciseIndex, setDraggedExerciseIndex] = useState(null);
  const [dropTargetExerciseIndex, setDropTargetExerciseIndex] = useState(null);
  const [prs, setPrs] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [prGoalWeekly, setPrGoalWeekly] = useState(() => {
    const raw = localStorage.getItem('prGoalWeekly');
    const n = raw ? parseInt(raw, 10) : 3;
    return Number.isFinite(n) && n > 0 ? n : 3;
  });
  const [prGoalWeeklyInput, setPrGoalWeeklyInput] = useState(() => {
    const raw = localStorage.getItem('prGoalWeekly');
    const n = raw ? parseInt(raw, 10) : 3;
    return String(Number.isFinite(n) && n > 0 ? n : 3);
  });
  const [selectedProgramDayName, setSelectedProgramDayName] = useState(null);
  const [showAddPr, setShowAddPr] = useState(false);
  const [prListExpanded, setPrListExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [newPrExercise, setNewPrExercise] = useState('');
  const [newPrWeight, setNewPrWeight] = useState('');
  const [newPrReps, setNewPrReps] = useState('');
  const [bodyweightLog, setBodyweightLog] = useState([]);
  const [bodyweightGoal, setBodyweightGoal] = useState(null);
  const [editingProgram, setEditingProgram] = useState(false);
  const [creatingProgram, setCreatingProgram] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [activeTab, setActiveTab] = useState('train');
  const [saveState, setSaveState] = useState('saved');
  const [nowTs, setNowTs] = useState(Date.now());
  const [focusAiAnalysis, setFocusAiAnalysis] = useState(false);
  const [guideSheetName, setGuideSheetName] = useState(null);
  const [restRemaining, setRestRemaining] = useState(null);
  const [restTotal, setRestTotal] = useState(90);
  const [defaultRest, setDefaultRest] = useState(() => {
    const n = parseInt(localStorage.getItem('defaultRestSeconds') || '90', 10);
    return Number.isFinite(n) && n > 0 ? n : 90;
  });
  const lastScrollY = useRef(0);
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const workoutLogRef = useRef(workoutLog);
  const finishedRef = useRef(workoutFinished);
  const dayNameRef = useRef(selectedProgramDayName);
  const exercisesRef = useRef(todaysExercises);
  const t = translations[language];
  const today = new Date().toLocaleDateString('en-GB');

  workoutLogRef.current = workoutLog;
  finishedRef.current = workoutFinished;
  dayNameRef.current = selectedProgramDayName;
  exercisesRef.current = todaysExercises;

  useEffect(() => { onAuthStateChanged(auth, (u) => setUser(u)); }, []);
  useEffect(() => { document.body.classList.toggle('light', !darkMode); }, [darkMode]);
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', darkMode ? '#111113' : '#f4f4f5');
  }, [darkMode]);
  useEffect(() => { localStorage.setItem('advancedTracking', advancedTracking); }, [advancedTracking]);

  useEffect(() => {
    if (activeTab !== 'progress' || !focusAiAnalysis) return undefined;
    const id = window.requestAnimationFrame(() => {
      document.getElementById('ai-analysis')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFocusAiAnalysis(false);
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeTab, focusAiAnalysis]);

  useEffect(() => {
    const threshold = 10;
    function onScroll() {
      const y = window.scrollY;
      if (y <= threshold) setHeaderHidden(false);
      else if (y > lastScrollY.current + threshold) setHeaderHidden(true);
      else if (y < lastScrollY.current - threshold) setHeaderHidden(false);
      lastScrollY.current = y;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!user) return;
    async function loadData() {
      const ref = doc(db, 'workouts', user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const snapData = snap.data() || {};
        const data = snapData.log || {};
        setWorkoutLog(data);
        workoutLogRef.current = data;
        if (snapData.activeProgram) {
          setActiveProgram(snapData.activeProgram);
          localStorage.setItem('activeProgram', JSON.stringify(snapData.activeProgram));
        }
        setPrs(Array.isArray(snapData.prs) ? snapData.prs : []);
        const rawBw = Array.isArray(snapData.bodyweightLog) ? snapData.bodyweightLog : [];
        const KG_TO_LBS = 2.2046226218;
        const toKg = (val, unit) => {
          const n = Number(val);
          if (!Number.isFinite(n)) return null;
          if (unit === 'kg') return n;
          if (unit === 'lbs') return n / KG_TO_LBS;
          return null;
        };
        const normalizedBw = rawBw.map((e) => {
          if (e && typeof e.weightKg === 'number') return e;
          const kg = toKg(e?.weight, e?.unit);
          if (kg === null) return e;
          return { ...e, weightKg: kg };
        });
        setBodyweightLog(normalizedBw);
        setBodyweightGoal(snapData.bodyweightGoal || null);
        if (typeof snapData.prGoalWeekly === 'number' && snapData.prGoalWeekly > 0) {
          setPrGoalWeekly(snapData.prGoalWeekly);
          setPrGoalWeeklyInput(String(snapData.prGoalWeekly));
          localStorage.setItem('prGoalWeekly', String(snapData.prGoalWeekly));
        }
        if (data[today]) {
          setTodaysExercises(data[today].exercises || []);
          setWorkoutFinished(data[today].finished || false);
          if (data[today].programDayName) {
            setSelectedProgramDayName(data[today].programDayName);
            setIgnoringProgram(true);
          } else if ((data[today].exercises || []).length > 0) {
            setIgnoringProgram(true);
          }
        }
      }
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (workoutFinished) return undefined;
    const startedAt = workoutLog[today]?.startedAt;
    if (!startedAt && todaysExercises.length === 0) return undefined;
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [workoutFinished, workoutLog, today, todaysExercises.length]);

  useEffect(() => {
    if (restRemaining == null) return undefined;
    if (restRemaining <= 0) {
      showToast({ title: t.restDone });
      try { navigator.vibrate?.(200); } catch { /* ignore */ }
      setRestRemaining(null);
      return undefined;
    }
    const id = window.setTimeout(() => setRestRemaining((v) => (v == null ? null : v - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [restRemaining, t.restDone]);

  useEffect(() => {
    function flush() {
      if (pendingSaveRef.current) {
        const job = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        saveToLog(job.exercises, job.finished, job.extras);
      }
    }
    function onVis() {
      if (document.visibilityState === 'hidden') flush();
    }
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, today]);

  function showToast(next) {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast(next);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3500);
  }

  function getExerciseMaxWeightInExercises(exercises, name) {
    let max = 0;
    (exercises || []).forEach((ex) => {
      if (ex.name !== name) return;
      (ex.sets || []).forEach((s) => {
        if (s.completed === false) return;
        const val = parseFloat(s.weight);
        if (!Number.isNaN(val)) max = Math.max(max, val);
      });
    });
    return max;
  }

  function getExerciseMaxWeightExcludingToday(name) {
    let max = 0;
    Object.entries(workoutLogRef.current || {}).forEach(([date, w]) => {
      if (date === today) return;
      (w.exercises || []).forEach((ex) => {
        if (ex.name !== name) return;
        (ex.sets || []).forEach((s) => {
          if (s.completed === false) return;
          const val = parseFloat(s.weight);
          if (!Number.isNaN(val)) max = Math.max(max, val);
        });
      });
    });
    return max;
  }

  async function addPrEntry(entry) {
    const updated = [entry, ...prs].slice(0, 200);
    setPrs(updated);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { prs: updated }, { merge: true });
    }
  }

  function maybeRecordPr(exerciseName, weightNum, reps) {
    if (!exerciseName || Number.isNaN(weightNum)) return;
    const prevMax = getExerciseMaxWeightExcludingToday(exerciseName);
    const sessionMaxBefore = getExerciseMaxWeightInExercises(todaysExercises, exerciseName);
    if (weightNum > Math.max(prevMax, sessionMaxBefore)) {
      const prEntry = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        date: today,
        exercise: exerciseName,
        weight: weightNum,
        reps: String(reps || ''),
        unit: weightUnit,
        dayName: dayNameRef.current || null,
      };
      addPrEntry(prEntry);
      showToast({ title: t.prToastTitle, body: `${prEntry.exercise}: ${prEntry.weight}${prEntry.unit}${prEntry.reps ? ` × ${prEntry.reps}` : ''}` });
    }
  }

  async function commitPrGoalWeekly(nextValue) {
    const n = parseInt(String(nextValue), 10);
    if (!Number.isFinite(n) || n <= 0) {
      setPrGoalWeeklyInput(String(prGoalWeekly));
      return;
    }
    setPrGoalWeekly(n);
    setPrGoalWeeklyInput(String(n));
    localStorage.setItem('prGoalWeekly', String(n));
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { prGoalWeekly: n }, { merge: true });
    }
  }

  async function removeTodayFromLog() {
    const updatedLog = { ...workoutLogRef.current };
    delete updatedLog[today];
    setWorkoutLog(updatedLog);
    workoutLogRef.current = updatedLog;
    localStorage.setItem('workoutLog', JSON.stringify(updatedLog));
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { log: updatedLog }, { merge: true });
    }
  }

  async function addBodyweightEntry(weightVal) {
    const KG_TO_LBS = 2.2046226218;
    const n = Number(weightVal);
    if (!Number.isFinite(n)) return;
    const weightKg = weightUnit === 'kg' ? n : n / KG_TO_LBS;
    const entry = { date: today, weightKg };
    const filtered = (bodyweightLog || []).filter((e) => e.date !== today);
    const updated = [entry, ...filtered].slice(0, 365);
    setBodyweightLog(updated);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { bodyweightLog: updated }, { merge: true });
    }
  }

  async function upsertBodyweightEntry(dateStr, weightVal) {
    const KG_TO_LBS = 2.2046226218;
    const n = Number(weightVal);
    if (!Number.isFinite(n)) return;
    const weightKg = weightUnit === 'kg' ? n : n / KG_TO_LBS;
    const entry = { date: String(dateStr || today), weightKg };
    const filtered = (bodyweightLog || []).filter((e) => e.date !== entry.date);
    const updated = [entry, ...filtered].slice(0, 365);
    setBodyweightLog(updated);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { bodyweightLog: updated }, { merge: true });
    }
  }

  async function deleteBodyweightEntry(dateStr) {
    const date = String(dateStr || '');
    if (!date) return;
    const updated = (bodyweightLog || []).filter((e) => e.date !== date);
    setBodyweightLog(updated);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { bodyweightLog: updated }, { merge: true });
    }
  }

  async function removePrEntry(id) {
    const updated = (prs || []).filter((p) => p.id !== id);
    setPrs(updated);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { prs: updated }, { merge: true });
    }
  }

  async function saveBodyweightGoal(goal) {
    setBodyweightGoal(goal);
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { bodyweightGoal: goal }, { merge: true });
    }
  }

  function getLastPerformance(name) {
    const dates = Object.keys(workoutLogRef.current || {})
      .filter((date) => date !== today && (workoutLogRef.current[date].exercises || []).some((ex) => ex.name === name))
      .sort((a, b) => {
        const da = parseEnGbDate(a);
        const db = parseEnGbDate(b);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return db - da;
      });
    if (dates.length === 0) return null;
    const exercise = workoutLogRef.current[dates[0]].exercises.find((ex) => ex.name === name);
    const sets = (exercise?.sets || []).filter((s) => s.completed !== false && ((s.reps && s.weight) || s.durationMinutes));
    return sets.length ? sets : null;
  }

  function prefillSets(name, trackingType, setsReps) {
    const timed = trackingType === 'timed' || TIMED_EXERCISES.has(name);
    const last = getLastPerformance(name) || [];
    const count = last.length || parseSetCount(setsReps);
    return Array.from({ length: count }, (_, i) => emptySet(timed, last[i] || last[last.length - 1]));
  }

  async function saveToLog(exercises, finished, extras = {}) {
    setSaveState('saving');
    const current = workoutLogRef.current || {};
    const prev = current[today] || {};
    const entry = {
      exercises,
      finished,
      programDayName: extras.programDayName !== undefined ? extras.programDayName : (dayNameRef.current || prev.programDayName || null),
      startedAt: extras.startedAt ?? prev.startedAt ?? Date.now(),
    };
    if (extras.durationSeconds != null) entry.durationSeconds = extras.durationSeconds;
    const updatedLog = { ...current, [today]: entry };
    workoutLogRef.current = updatedLog;
    setWorkoutLog(updatedLog);
    localStorage.setItem('workoutLog', JSON.stringify(updatedLog));
    try {
      if (user) {
        const ref = doc(db, 'workouts', user.uid);
        await setDoc(ref, { log: updatedLog }, { merge: true });
      }
      setSaveState('saved');
    } catch {
      setSaveState('saved');
    }
  }

  function scheduleSave(exercises, finished = finishedRef.current, extras = {}) {
    pendingSaveRef.current = { exercises, finished, extras };
    setSaveState('saving');
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      const job = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (job) saveToLog(job.exercises, job.finished, job.extras);
    }, 400);
  }

  async function syncActiveProgramDay(dayName, exercises) {
    if (!activeProgram || !dayName) return;
    const updatedDays = activeProgram.days.map((d) =>
      d.dayName === dayName
        ? { ...d, exercises: exercises.map(({ name, setsReps = '', tip = '', trackingType = 'weighted', primaryMuscle }) => ({ name, setsReps, tip, trackingType, primaryMuscle })) }
        : d
    );
    const saved = { ...activeProgram, days: updatedDays };
    setActiveProgram(saved);
    localStorage.setItem('activeProgram', JSON.stringify(saved));
    if (user) {
      const ref = doc(db, 'workouts', user.uid);
      await setDoc(ref, { activeProgram: saved }, { merge: true });
    }
  }

  function handleExerciseInput(value) {
    setExerciseName(value);
    if (value.length < 2) { setSuggestions([]); return; }
    const q = value.toLowerCase();
    const history = [...new Set(
      Object.values(workoutLogRef.current || {}).flatMap((w) => w.exercises?.map((e) => e.name) || [])
    )];
    const fromHistory = history.filter((name) => name.toLowerCase().includes(q));
    const fromPresets = PRESET_EXERCISES.filter((name) => (
      name.toLowerCase().includes(q) && !fromHistory.some((h) => h.toLowerCase() === name.toLowerCase())
    ));
    const used = new Set([...fromHistory, ...fromPresets].map((name) => name.toLowerCase()));
    const fromCatalog = searchGuideExercises(value, 6)
      .map((ex) => displayNameForGuide(ex))
      .filter((name) => name && !used.has(name.toLowerCase()));
    const merged = [];
    const seen = new Set();
    [...fromHistory, ...fromPresets, ...fromCatalog].forEach((name) => {
      const key = name.toLowerCase();
      if (seen.has(key) || merged.length >= 6) return;
      seen.add(key);
      merged.push(name);
    });
    setSuggestions(merged);
  }

  function openExerciseGuide(name) {
    if (name) setGuideSheetName(name);
  }

  function pickExerciseFromMap(name) {
    if (!name) return;
    setExerciseName(name);
    setSuggestions([]);
    setCustomMuscle('');
    setIgnoringProgram(true);
    setActiveTab('train');
  }

  function selectSuggestion(name) {
    setExerciseName(name);
    setSuggestions([]);
    setCustomMuscle('');
    if (TIMED_EXERCISES.has(name)) setNewExerciseTrackingType('timed');
  }

  function addNamedExercise() {
    const name = exerciseName.trim();
    if (!name) return;
    const timed = newExerciseTrackingType === 'timed' || TIMED_EXERCISES.has(name);
    const trackingType = timed ? 'timed' : 'weighted';
    const known = getExerciseMuscles(name);
    const ex = {
      name,
      sets: prefillSets(name, trackingType),
      trackingType,
    };
    if (!known.primary.length && customMuscle) ex.primaryMuscle = customMuscle;
    const updated = [...todaysExercises, ex];
    setTodaysExercises(updated);
    setIgnoringProgram(true);
    saveToLog(updated, false, { startedAt: workoutLogRef.current[today]?.startedAt || Date.now() });
    setExerciseName('');
    setSuggestions([]);
    setCustomMuscle('');
    setNewExerciseTrackingType('weighted');
  }

  function pickProgramDay(day) {
    const exercises = (day.exercises || []).map((ex) => ({
      name: ex.name,
      sets: prefillSets(ex.name, ex.trackingType || (TIMED_EXERCISES.has(ex.name) ? 'timed' : 'weighted'), ex.setsReps),
      setsReps: ex.setsReps || '',
      tip: ex.tip || '',
      trackingType: ex.trackingType || (TIMED_EXERCISES.has(ex.name) ? 'timed' : 'weighted'),
      primaryMuscle: ex.primaryMuscle,
    }));
    setTodaysExercises(exercises);
    setSelectedProgramDayName(day.dayName);
    dayNameRef.current = day.dayName;
    setIgnoringProgram(true);
    saveToLog(exercises, false, { programDayName: day.dayName, startedAt: Date.now() });
  }

  function loadPreviousWorkout(date) {
    const source = workoutLogRef.current[date];
    const exercises = (source.exercises || []).map((ex) => ({
      name: ex.name,
      sets: prefillSets(ex.name, ex.trackingType, ex.setsReps),
      setsReps: ex.setsReps || '',
      tip: ex.tip || '',
      trackingType: ex.trackingType || 'weighted',
      primaryMuscle: ex.primaryMuscle,
    }));
    setTodaysExercises(exercises);
    setShowLoadPrevious(false);
    setIgnoringProgram(true);
    saveToLog(exercises, false, { startedAt: Date.now() });
  }

  function updateSet(exIndex, setIndex, field, value) {
    const updated = todaysExercises.map((ex, i) => {
      if (i !== exIndex) return ex;
      return {
        ...ex,
        sets: ex.sets.map((s, j) => (j === setIndex ? { ...s, [field]: value } : s)),
      };
    });
    setTodaysExercises(updated);
    scheduleSave(updated);
  }

  function fillSetFromPrevious(exIndex, setIndex) {
    const ex = todaysExercises[exIndex];
    if (!ex) return;
    const last = getLastPerformance(ex.name) || [];
    const prev = last[setIndex] || last[last.length - 1];
    if (!prev) return;
    const timed = isTimedExercise(ex) || prev.durationMinutes;
    const updated = todaysExercises.map((item, i) => {
      if (i !== exIndex) return item;
      return {
        ...item,
        sets: item.sets.map((s, j) => {
          if (j !== setIndex) return s;
          if (timed) {
            return { ...s, durationMinutes: prev.durationMinutes || '', completed: false };
          }
          return {
            ...s,
            reps: prev.reps || '',
            weight: prev.weight !== undefined && prev.weight !== '' ? prev.weight : '',
            rir: prev.rir || s.rir || '',
            completed: false,
          };
        }),
      };
    });
    setTodaysExercises(updated);
    scheduleSave(updated);
  }

  function addSetRow(exIndex) {
    const updated = todaysExercises.map((ex, i) => {
      if (i !== exIndex) return ex;
      const last = ex.sets[ex.sets.length - 1];
      return { ...ex, sets: [...ex.sets, emptySet(isTimedExercise(ex), last)] };
    });
    setTodaysExercises(updated);
    saveToLog(updated, false);
  }

  function deleteSetRow(exIndex, setIndex) {
    const updated = todaysExercises.map((ex, i) => {
      if (i !== exIndex) return ex;
      const nextSets = ex.sets.filter((_, j) => j !== setIndex);
      return { ...ex, sets: nextSets.length ? nextSets : [emptySet(isTimedExercise(ex))] };
    });
    setTodaysExercises(updated);
    saveToLog(updated, false);
  }

  function toggleComplete(exIndex, setIndex) {
    const exercise = todaysExercises[exIndex];
    const set = exercise.sets[setIndex];
    const currentlyComplete = isSetComplete(exercise, set);
    if (!currentlyComplete && !isSetFilled(exercise, set)) return;
    const updated = todaysExercises.map((ex, i) => {
      if (i !== exIndex) return ex;
      return {
        ...ex,
        sets: ex.sets.map((s, j) => (j === setIndex ? { ...s, completed: !currentlyComplete, rest: !currentlyComplete && !isTimedExercise(ex) ? defaultRest : s.rest } : s)),
      };
    });
    setTodaysExercises(updated);
    saveToLog(updated, false);
    if (!currentlyComplete && !isTimedExercise(exercise)) {
      maybeRecordPr(exercise.name, parseFloat(set.weight), set.reps);
      setRestTotal(defaultRest);
      setRestRemaining(defaultRest);
    }
  }

  function deleteExercise(index) {
    const updated = todaysExercises.filter((_, i) => i !== index);
    setTodaysExercises(updated);
    saveToLog(updated, false);
  }

  function reorderExercises(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    const newOrder = [...todaysExercises];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setTodaysExercises(newOrder);
    saveToLog(newOrder, false);
    setDropTargetExerciseIndex(null);
  }

  function finishWorkout() {
    if (!workoutHasLoggedSets({ exercises: todaysExercises })) return;
    const started = workoutLogRef.current[today]?.startedAt || Date.now();
    const durationSeconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
    saveToLog(todaysExercises, true, { durationSeconds });
    syncActiveProgramDay(dayNameRef.current, todaysExercises);
    setWorkoutFinished(true);
    setRestRemaining(null);
  }

  function reopenWorkout() {
    const duration = workoutLogRef.current[today]?.durationSeconds || 0;
    setWorkoutFinished(false);
    saveToLog(todaysExercises, false, { startedAt: Date.now() - duration * 1000, durationSeconds: duration });
  }

  async function handleShareWorkout(exercises = todaysExercises, date = today, meta = workoutLogRef.current[today]) {
    try {
      const recap = buildSessionRecap(workoutLogRef.current, date, meta || { exercises }, weightUnit);
      const vsLabel = recap.volumeChangePct == null
        ? ''
        : `${recap.volumeChangePct >= 0 ? '+' : ''}${recap.volumeChangePct.toFixed(1)}% ${t.recapVsLast}${recap.comparedDayName || recap.comparedDate ? ` · ${recap.comparedDayName || recap.comparedDate}` : ''}`;
      await shareWorkoutCard({
        date,
        dayName: meta?.programDayName,
        exercises,
        volume: workoutVolume(meta || { exercises }),
        durationLabel: meta?.durationSeconds ? `${Math.round(meta.durationSeconds / 60)} min` : '',
        vsLabel,
        language,
      });
    } catch {
      showToast({ title: t.shareFailed });
    }
  }

  async function handleShareStats({ volumeLabel, changeLabel, bars }) {
    try {
      await shareStatsCard({
        title: t.weeklyVolume,
        subtitle: t.last7,
        volumeLabel,
        changeLabel,
        bars: bars.map((b) => ({ label: b.label, value: b.value })),
        language,
      });
    } catch {
      showToast({ title: t.shareFailed });
    }
  }

  const todayEntry = workoutLog[today] || {};
  const startedAt = todayEntry.startedAt;
  const elapsedSeconds = workoutFinished
    ? (todayEntry.durationSeconds || 0)
    : startedAt
      ? Math.max(0, Math.floor((nowTs - startedAt) / 1000))
      : 0;

  const totalWorkouts = Object.values(workoutLog).filter((w) => w.finished || workoutHasLoggedSets(w)).length;
  const totalExercises = Object.values(workoutLog).reduce((acc, w) => acc + (w.exercises?.length || 0), 0);
  const totalSets = Object.values(workoutLog).reduce((acc, w) => acc + countLoggedSets(w.exercises), 0);
  const previousDates = Object.keys(workoutLog)
    .filter((date) => date !== today && workoutHasContent(workoutLog[date]))
    .sort((a, b) => {
      const da = parseEnGbDate(a);
      const db = parseEnGbDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
  const previousWorkoutDates = previousDates.slice(0, 7);
  const prList = prs || [];
  const visiblePrs = prListExpanded ? prList : prList.slice(0, 3);
  const visibleHistoryDates = historyExpanded ? previousDates : previousDates.slice(0, 3);

  function renderSets(sets, trackingType = 'weighted') {
    return (sets || []).filter(isSetLogged).map((s, j) => (
      <div key={j} className="set-row">
        <span>
          <span className="set-badge">{t.set} {j + 1}</span>
          {(trackingType === 'timed' || s.durationMinutes)
            ? `${s.durationMinutes} ${t.min}`
            : `${s.reps} × ${s.weight}${weightUnit}`}
        </span>
      </div>
    ));
  }

  if (!user) return <Auth darkMode={darkMode} />;

  return (
    <div className={`app app--tabbed${headerHidden ? ' app--header-hidden' : ''}`}>
      <header className={`header${headerHidden ? ' header--hidden' : ''}`}>
        <img src={darkMode ? logoLight : logoDark} alt="LiftLab" />
        <div className="header-actions">
          <button className="theme-toggle" onClick={() => { const newLang = language === 'en' ? 'da' : 'en'; setLanguage(newLang); localStorage.setItem('language', newLang); }}>
            {language === 'en' ? '🇩🇰' : '🇬🇧'}
          </button>
          <button className="theme-toggle" onClick={() => { const newMode = !darkMode; setDarkMode(newMode); localStorage.setItem('darkMode', newMode); }}>
            {darkMode ? t.light : t.dark}
          </button>
          <button className="theme-toggle" onClick={() => signOut(auth)}>
            {t.signOut}
          </button>
        </div>
      </header>

      {activeTab === 'train' && <AddToHomeScreen language={language} />}

      {toast && (
        <div className="pr-toast" role="status" aria-live="polite">
          <div className="pr-toast-title">{toast.title}</div>
          {toast.body && <div className="pr-toast-body">{toast.body}</div>}
        </div>
      )}

      {activeTab === 'train' && (
        <WorkoutSession
          t={t}
          language={language}
          today={today}
          weightUnit={weightUnit}
          advancedTracking={advancedTracking}
          onToggleWeightUnit={() => {
            const next = weightUnit === 'kg' ? 'lbs' : 'kg';
            setWeightUnit(next);
            localStorage.setItem('weightUnit', next);
          }}
          onToggleAdvanced={() => setAdvancedTracking(!advancedTracking)}
          activeProgram={activeProgram}
          ignoringProgram={ignoringProgram}
          onPickProgramDay={pickProgramDay}
          onLogManually={() => setIgnoringProgram(true)}
          onViewProgram={() => { setEditingProgram(false); setCreatingProgram(false); setActiveTab('program'); }}
          todaysExercises={todaysExercises}
          workoutFinished={workoutFinished}
          onUpdateSet={updateSet}
          onAddSetRow={addSetRow}
          onDeleteSet={deleteSetRow}
          onToggleComplete={toggleComplete}
          onDeleteExercise={deleteExercise}
          onFinish={finishWorkout}
          onReopen={reopenWorkout}
          onDeleteWorkout={() => {
            setTodaysExercises([]);
            setWorkoutFinished(false);
            setIgnoringProgram(false);
            setSelectedProgramDayName(null);
            dayNameRef.current = null;
            removeTodayFromLog();
          }}
          onBack={async () => {
            setTodaysExercises([]);
            setWorkoutFinished(false);
            setIgnoringProgram(false);
            setSelectedProgramDayName(null);
            dayNameRef.current = null;
            setShowLoadPrevious(false);
            await removeTodayFromLog();
          }}
          getLastPerformance={getLastPerformance}
          exerciseName={exerciseName}
          suggestions={suggestions}
          onExerciseInput={handleExerciseInput}
          onSelectSuggestion={selectSuggestion}
          onAddExercise={addNamedExercise}
          onOpenExercise={openExerciseGuide}
          newExerciseTrackingType={newExerciseTrackingType}
          onTrackingType={setNewExerciseTrackingType}
          customMuscle={customMuscle}
          onCustomMuscle={setCustomMuscle}
          saveState={saveState}
          elapsedSeconds={elapsedSeconds}
          defaultRest={defaultRest}
          onChangeDefaultRest={(s) => {
            setDefaultRest(s);
            localStorage.setItem('defaultRestSeconds', String(s));
          }}
          selectedProgramDayName={selectedProgramDayName}
          showLoadPrevious={showLoadPrevious}
          onToggleLoadPrevious={() => setShowLoadPrevious((v) => !v)}
          onLoadPrevious={loadPreviousWorkout}
          previousWorkoutDates={previousWorkoutDates}
          workoutLog={workoutLog}
          onShare={() => handleShareWorkout()}
          recap={buildSessionRecap(
            workoutLog,
            today,
            {
              ...(workoutLog[today] || {}),
              exercises: todaysExercises,
              programDayName: selectedProgramDayName || workoutLog[today]?.programDayName || null,
              durationSeconds: elapsedSeconds,
            },
            weightUnit
          )}
          onFillFromPrevious={fillSetFromPrevious}
          onRequestAiFeedback={() => {
            setFocusAiAnalysis(true);
            setActiveTab('progress');
          }}
          draggedExerciseIndex={draggedExerciseIndex}
          dropTargetExerciseIndex={dropTargetExerciseIndex}
          onDragStart={(e, i) => {
            e.dataTransfer.setData('text/plain', String(i));
            e.dataTransfer.effectAllowed = 'move';
            setDraggedExerciseIndex(i);
          }}
          onDragEnd={() => { setDraggedExerciseIndex(null); setDropTargetExerciseIndex(null); }}
          onDragOver={(e, i) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (draggedExerciseIndex !== null) setDropTargetExerciseIndex(i);
          }}
          onDrop={(e, i) => {
            e.preventDefault();
            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
            if (fromIndex !== i) reorderExercises(fromIndex, i);
            setDraggedExerciseIndex(null);
            setDropTargetExerciseIndex(null);
          }}
        />
      )}

      {activeTab === 'progress' && (
        <div className="progress-tab">
          <div className="stats-bar">
            <div className="stat-card"><div className="stat-number">{totalWorkouts}</div><div className="stat-label">{t.workouts}</div></div>
            <div className="stat-card"><div className="stat-number">{totalExercises}</div><div className="stat-label">{t.totalExercises}</div></div>
            <div className="stat-card"><div className="stat-number">{totalSets}</div><div className="stat-label">{t.totalSets}</div></div>
          </div>

          <p className="section-title">{t.exerciseProgress}</p>
          <div className="card">
            <ProgressCharts workoutLog={workoutLog} language={language} weightUnit={weightUnit} />
          </div>

          <p className="section-title">{t.muscleGuide}</p>
          <div className="card">
            <MuscleDistribution workoutLog={workoutLog} language={language} weightUnit={weightUnit} t={t} onShare={handleShareStats} onPickExercise={pickExerciseFromMap} onOpenExercise={openExerciseGuide} />
          </div>

          <div className="card pr-card">
            <div className="pr-card-header">
              <div>
                <p className="pr-card-title">{t.prTrackerTitle}</p>
                <p className="pr-card-subtitle">{t.prTrackerSubtitle}</p>
              </div>
              <button type="button" className="btn btn-dark" onClick={() => setShowAddPr((v) => !v)}>
                {showAddPr ? t.prClose : t.prAdd}
              </button>
            </div>
            {(() => {
              const now = new Date();
              const weekStart = getWeekStart(now);
              const countThisWeek = (prs || []).filter((p) => {
                const d = parseEnGbDate(p.date);
                return d && d >= weekStart;
              }).length;
              const goal = prGoalWeekly || 3;
              const pct = Math.min(100, Math.round((countThisWeek / goal) * 100));
              return (
                <div className="pr-week">
                  <div className="pr-week-row">
                    <div className="pr-week-title">{t.prThisWeek}</div>
                    <div className="pr-week-meta">{countThisWeek}/{goal} {t.prUnit}</div>
                  </div>
                  <div className="pr-week-bar"><div className="pr-week-bar-fill" style={{ width: `${pct}%` }} /></div>
                  <div className="pr-week-goal">
                    <span>{t.prWeeklyGoal}</span>
                    <input
                      className="pr-week-goal-input"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={prGoalWeeklyInput}
                      onChange={(e) => {
                        const next = String(e.target.value || '');
                        if (next === '') { setPrGoalWeeklyInput(''); return; }
                        setPrGoalWeeklyInput(next.replace(/[^0-9]/g, ''));
                      }}
                      onBlur={() => commitPrGoalWeekly(prGoalWeeklyInput)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    />
                  </div>
                </div>
              );
            })()}
            {showAddPr && (
              <div className="pr-add-form">
                <input className="input-field wide" placeholder={t.prFormExercise} value={newPrExercise} onChange={(e) => setNewPrExercise(e.target.value)} />
                <div className="input-row">
                  <input className="input-field narrow" placeholder={weightUnit} value={newPrWeight} onChange={(e) => setNewPrWeight(e.target.value)} />
                  <input className="input-field narrow" placeholder={t.prFormReps} value={newPrReps} onChange={(e) => setNewPrReps(e.target.value)} />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const w = parseFloat(newPrWeight);
                      if (!newPrExercise.trim() || Number.isNaN(w)) return;
                      const prEntry = {
                        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        date: today,
                        exercise: newPrExercise.trim(),
                        weight: w,
                        reps: String(newPrReps || ''),
                        unit: weightUnit,
                        dayName: selectedProgramDayName || null,
                      };
                      addPrEntry(prEntry);
                      showToast({ title: t.prToastTitle, body: `${prEntry.exercise}: ${prEntry.weight}${prEntry.unit}` });
                      setNewPrExercise(''); setNewPrWeight(''); setNewPrReps('');
                      setShowAddPr(false);
                    }}
                  >
                    {t.prFormSave}
                  </button>
                </div>
              </div>
            )}
            <div className="pr-list">
              {visiblePrs.map((p) => (
                <div key={p.id} className="pr-item">
                  <div className="pr-item-main">
                    <div className="pr-item-exercise">{p.exercise}</div>
                    <div className="pr-item-meta">{p.date}{p.dayName ? ` · ${p.dayName}` : ''} · {p.weight}{p.unit}{p.reps ? ` × ${p.reps}` : ''}</div>
                  </div>
                  <button type="button" className="btn-icon pr-delete" onClick={() => removePrEntry(p.id)} aria-label={t.prDeleteAria}>
                    <IconTrash size={16} />
                  </button>
                </div>
              ))}
              {prList.length === 0 && <div className="pr-empty">{t.prNone}</div>}
            </div>
            {prList.length > 3 && (
              <button type="button" className="btn btn-dark list-expand-toggle" onClick={() => setPrListExpanded((v) => !v)}>
                {prListExpanded ? t.showLess : t.showMore}
              </button>
            )}
          </div>

          <p className="section-title">{t.bodyweight}</p>
          <div className="card">
            <BodyWeightTracker
              language={language}
              weightUnit={weightUnit}
              entries={bodyweightLog}
              goal={bodyweightGoal}
              onAddEntry={addBodyweightEntry}
              onUpsertEntry={upsertBodyweightEntry}
              onDeleteEntry={deleteBodyweightEntry}
              onSaveGoal={saveBodyweightGoal}
            />
          </div>

          <p className="section-title" id="ai-analysis">{t.aiAnalysis}</p>
          <div className="card">
            <AIAnalysis
              workoutLog={workoutLog}
              bodyweightLog={bodyweightLog}
              bodyweightGoal={bodyweightGoal}
              language={language}
              user={user}
              recap={buildSessionRecap(workoutLog, today, workoutLog[today], weightUnit)}
              muscleHeat7={muscleSetsInRange(workoutLog, 7)}
              muscleHeat30={muscleSetsInRange(workoutLog, 30)}
            />
          </div>

          {previousDates.length > 0 && (
            <>
              <p className="section-title">{t.history}</p>
              {visibleHistoryDates.map((date) => (
                <div key={date} className={`card history${!workoutLog[date].finished ? ' is-incomplete' : ''}`} onClick={() => setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }))} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }))}>
                  <div className="card-header">
                    <div>
                      <p className="exercise-name">{date}</p>
                      <p className="finished-summary">
                        {(workoutLog[date].exercises || []).length} {t.exercises} · {countLoggedSets(workoutLog[date].exercises)} {t.sets}
                        {!workoutLog[date].finished ? ` · ${t.incomplete}` : ''}
                      </p>
                    </div>
                    <span className={`chevron ${expandedDates[date] ? 'open' : ''}`}>▼</span>
                  </div>
                  <div className={`expandable ${expandedDates[date] ? 'open' : ''}`}>
                    <div className="expandable-content">
                      {(workoutLog[date].exercises || []).map((ex, i) => (
                        <div key={i}>
                          <p className="exercise-name">{ex.name}</p>
                          {ex.setsReps && <p className="exercise-prescription">{t.repsLabel} {ex.setsReps}</p>}
                          {renderSets(ex.sets, ex.trackingType)}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-dark"
                        style={{ marginTop: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareWorkout(workoutLog[date].exercises, date, workoutLog[date]);
                        }}
                      >
                        <IconShare size={16} /> {t.share}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {previousDates.length > 3 && (
                <button type="button" className="btn btn-dark list-expand-toggle" onClick={(e) => { e.stopPropagation(); setHistoryExpanded((v) => !v); }}>
                  {historyExpanded ? t.showLess : t.showMore}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'program' && (
        <div className="program-tab">
          {activeProgram && !editingProgram && !creatingProgram && (
            <ActiveProgramOverview
              program={activeProgram}
              selectedDayName={selectedProgramDayName}
              t={t}
              language={language}
              onEdit={() => { setEditingProgram(true); setCreatingProgram(false); }}
              onRemove={async () => {
                setActiveProgram(null);
                localStorage.removeItem('activeProgram');
                setIgnoringProgram(false);
                if (user) {
                  const ref = doc(db, 'workouts', user.uid);
                  await setDoc(ref, { activeProgram: null }, { merge: true });
                }
              }}
              onCreateNew={() => { setCreatingProgram(true); setEditingProgram(false); }}
              onOpenExercise={openExerciseGuide}
            />
          )}
          {(!activeProgram || editingProgram || creatingProgram) && (
            <>
              <p className="section-title">{editingProgram ? t.editProgram : t.programBuilder}</p>
              {creatingProgram && (
                <button type="button" className="btn btn-dark" style={{ marginBottom: 12 }} onClick={() => setCreatingProgram(false)}>
                  {t.cancel}
                </button>
              )}
              <div className="card">
                <ProgramBuilder
                  key={editingProgram ? 'edit-program' : creatingProgram ? 'replace-program' : 'new-program'}
                  language={language}
                  user={user}
                  initialProgram={editingProgram ? activeProgram?.days : null}
                  onCancelEdit={() => { setEditingProgram(false); setCreatingProgram(false); }}
                  onOpenExercise={openExerciseGuide}
                  onProgramConfirmed={async (days) => {
                    const savedProgram = { days, confirmedAt: new Date().toLocaleDateString('en-GB') };
                    setActiveProgram(savedProgram);
                    localStorage.setItem('activeProgram', JSON.stringify(savedProgram));
                    if (user) {
                      const ref = doc(db, 'workouts', user.uid);
                      await setDoc(ref, { activeProgram: savedProgram }, { merge: true });
                    }
                    setEditingProgram(false);
                    setCreatingProgram(false);
                    showToast({ title: t.programSaved });
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {guideSheetName && (
        <GuideSheet
          name={guideSheetName}
          t={t}
          language={language}
          onClose={() => setGuideSheetName(null)}
        />
      )}

      {restRemaining != null && restRemaining >= 0 && (
        <div className="rest-timer" role="status">
          <div className="rest-timer-main">
            <span className="rest-timer-label">{t.restTimer}</span>
            <span className="rest-timer-count">{formatDuration(restRemaining)}</span>
            <div className="rest-timer-bar">
              <div className="rest-timer-bar-fill" style={{ width: `${restTotal ? Math.max(0, (restRemaining / restTotal) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="rest-timer-actions">
            <button type="button" className="btn btn-dark" onClick={() => setRestRemaining((v) => (v == null ? v : Math.max(0, v - 15)))}>-15</button>
            <button type="button" className="btn btn-dark" onClick={() => {
              setRestRemaining((v) => {
                if (v == null) return v;
                const next = v + 15;
                setRestTotal((total) => Math.max(next, total));
                return next;
              });
            }}>+15</button>
            <button type="button" className="btn btn-primary" onClick={() => setRestRemaining(null)}>{t.skipRest}</button>
          </div>
        </div>
      )}

      <BottomNav activeTab={activeTab} onChange={setActiveTab} t={t} />
    </div>
  );
}

export default App;
